import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, merge, Observable, of, ReplaySubject } from 'rxjs';
import { User } from '../_models/user';
import { JwtHelperService } from '@auth0/angular-jwt';
import { map, mergeMap } from 'rxjs/operators';
import { FilterService } from './filter.service';
import { DynamicTableService } from './dynamic-table.service';
import { DocumentsService } from './documents.service';
import { UserService } from './user.service';
import { AppService } from './app.service';
import { TextFiltersService } from './text-filters.service';
import { MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { teamConfig } from './sso.config';
import * as sjcl from 'sjcl';
import moment from 'moment';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
const helper = new JwtHelperService();
import { ErrorDiagnosticService } from './error-diagnostic.service';
import { AppInsightService } from './app-insight.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser$: Observable<User>;
  private tokenRefreshWorker!: Worker;
  private tokenRefreshTimeoutId: any = null;
  private msalInitPromise: Promise<void> | null = null;
  
  constructor(
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public jwtHelper: JwtHelperService,
    public appInsightService : AppInsightService,
    private http: HttpClient,
    private dynamicTableService: DynamicTableService,
    private documentsService: DocumentsService,
    private userService: UserService,
    private filterService: FilterService,
    private router: Router,
    private appService: AppService,
    private oauthService: OAuthService,
    private textFiltersService: TextFiltersService,
    private diagnosticService: ErrorDiagnosticService) {
    this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('user')));
    
    if(this.currentUserSubject.value){
      this.diagnosticService.setUser(this.currentUserSubject.value.name);
    }
    
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  public isAuthenticated(): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!!!currentUser?.provider){
      return false;
    } 
    
    const currentToken = currentUser.provider === 'TEAM' ? currentUser.authPayload.token.id_token : currentUser.token;
    if (!currentToken) return false;
    try {
      //return !helper.isTokenExpired(currentToken) && currentUser.hasOwnProperty('selectedLocationIndex');
      return !helper.isTokenExpired(currentToken) && (currentUser.hasOwnProperty('locationRoles') == currentUser.hasOwnProperty('selectedLocationIndex'));
    } catch {
      return false;
    }
  }

  public get currentUserValue(): User {
    const currentUser = this.currentUserSubject.value;
    let tokenExpired = false;
    
    try {
      if (!currentUser) {
        tokenExpired = true;
      } else if (currentUser.provider === 'TEAM') {
        const idToken = currentUser?.authPayload?.token?.id_token;
        if (idToken) {
          console.log('currentUser?.expiresAt : ' + currentUser?.expiresAt );
          tokenExpired = helper.isTokenExpired(idToken) as boolean;
        } else if (currentUser?.expiresAt) {
          tokenExpired = new Date(new Date().toUTCString()).getTime() >= Number(currentUser.expiresAt);
        } else {
          tokenExpired = true;
        }
      } else {
        tokenExpired = !!currentUser?.token && (helper.isTokenExpired(currentUser.token) as boolean);
      }
    } catch {
      tokenExpired = true;
    }

    if (tokenExpired) {
      this.appInsightService.logTrace("limpieza de localStorage", this.diagnosticService.getDetails());
      localStorage.clear();
      this.appInsightService.logTrace("loginRedirectAd desde auth-guard.guard", this.diagnosticService.getDetails());
    }

    return this.currentUserSubject.value; 
  }

  getCurrentLocation() {
    const hasLocationRoles = this.currentUserSubject.value && this.currentUserSubject.value.locationRoles && this.currentUserSubject.value.locationRoles.length > 0;
    let current = hasLocationRoles ? this.currentUserSubject.value.locationRoles.filter(role => role.Selected) : null;
    return current && current.length > 0 ? current[0] : null;
  }

  changeCurrentLocation(id) {
    let newUser: User = JSON.parse(JSON.stringify(this.currentUserSubject.value));
    let selectedIndex = 0;
    newUser.locationRoles.forEach(role => {
      role.Selected = role.Id === id;
      newUser.selectedLocationIndex = role.Id === id ? selectedIndex : newUser.selectedLocationIndex;
      selectedIndex++;
    });
    this.diagnosticService.setLocation(newUser.locationRoles[newUser.selectedLocationIndex].LocationName);
    localStorage.setItem('user', JSON.stringify(newUser));
    this.currentUserSubject.next(newUser);
    this.userService.checkLanguage();
  }

  getProfile(locationId, roleId) {
    // return of(mockProfile).pipe(delay(2000),
    return this.getConfigData(locationId, roleId).pipe(
      map((configData: any) => {
        const normalizedConfig = this.normalizeProfileConfig(configData);
        this.filterService.initDocStatus(normalizedConfig.docStatus);
        this.filterService.initScreenFilters(normalizedConfig.screenFilters);
        this.filterService.initFilters(normalizedConfig.filters, normalizedConfig.screenFilters);
        this.dynamicTableService.loadTableConfig(normalizedConfig);
        return normalizedConfig;
      })
    )
  }

  getProfileDriver() {
    return this.getConfigDriverData().pipe(
      map((configData: any) => {
        const normalizedConfig = this.normalizeProfileConfig(configData);
        this.filterService.initDocStatus(normalizedConfig.docStatus);
        this.filterService.initScreenFilters(normalizedConfig.screenFilters);
        this.filterService.initFilters(normalizedConfig.filters, normalizedConfig.screenFilters);
        this.dynamicTableService.loadTableConfig(normalizedConfig);
        return normalizedConfig;
      })
    )
  }
    
  loadMocks(configData) {
    this.filterService.loadMockFilters();
    this.filterService.loadMockConfigFilters();
  }

  getCurrentUser() {
    return this.currentUserSubject.value;
  }

  getConfigData(locationid, roleid) {
    return this.http.get<any>(`${environment.apiUrl}/api/filter/generalUI/${locationid}/${roleid}`).pipe(
      map(data => {
        const buttons = data?.button || data?.Button || [];
        const mapBtn = Array.isArray(buttons)
          ? buttons.map(b => b?.name?.toLowerCase() === 'reject' ? ({ ...b, icon: 'not_interested' }) : b)
          : [];
        const map = { ...data, button: mapBtn }
        return map;
      })
    );
  }

  getConfigDriverData() {
    var url = `${environment.apiUrl}/api/driversign/generalUI/1/1`;
    return this.http.get<any>(url).pipe(
      map(data => {
          const buttons = data?.button || data?.Button || [];
          const mapBtn = Array.isArray(buttons)
            ? buttons.map(b => b?.name?.toLowerCase() === 'reject' ? ({ ...b, icon: 'not_interested' }) : b)
            : [];
          const map = { ...data, button: mapBtn }
          return map;
      })
    );
  }

  private normalizeProfileConfig(configData: any) {
    const screenFilters =
      configData?.screenFilters
      || configData?.ScreenFilters
      || configData?.screenFilter
      || configData?.ScreenFilter
      || this.findNestedObject(configData, (value: any) => !!value?.dateRange || !!value?.DateRange)
      || { dateRange: { from: 0, to: 0 } };

    const docStatus =
      configData?.docStatus
      || configData?.DocStatus
      || this.findNestedArray(configData, (item: any) => item?.name || item?.Name)
      || configData?.status
      || configData?.Status
      || [];

    const filters =
      configData?.filters
      || configData?.Filters
      || configData?.filter
      || configData?.Filter
      || this.findNestedArray(configData, (item: any) =>
        !!(
          item?.name
          || item?.Name
          || item?.filterName
          || item?.FilterName
          || item?.field
          || item?.Field
          || item?.apiSearch
          || item?.ApiSearch
          || item?.searchKey
          || item?.SearchKey
          || item?.id
          || item?.Id
        )
      )
      || [];

    const normalized = {
      ...configData,
      screenFilters,
      docStatus,
      filters
    };

    if (!Array.isArray(normalized.filters) || normalized.filters.length === 0) {
      console.warn('generalUI returned no filters after normalization', {
        topLevelKeys: configData ? Object.keys(configData) : [],
        screenFiltersPresent: !!normalized.screenFilters,
        docStatusCount: Array.isArray(normalized.docStatus) ? normalized.docStatus.length : 0
      });
    }

    return normalized;
  }

  private findNestedArray(source: any, predicate: (item: any) => boolean): any[] | null {
    if (!source || typeof source !== 'object') {
      return null;
    }

    const values = Object.values(source);
    for (const value of values) {
      if (Array.isArray(value) && value.length && predicate(value[0])) {
        return value;
      }
    }

    for (const value of values) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const nested = this.findNestedArray(value, predicate);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  }

  private findNestedObject(source: any, predicate: (obj: any) => boolean): any {
    if (!source || typeof source !== 'object') {
      return null;
    }

    if (predicate(source)) {
      return source;
    }

    const values = Object.values(source);
    for (const value of values) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const nested = this.findNestedObject(value, predicate);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  }

/*
  teamAuth(params) {
    let body = `client_id=${environment.team.clientId}` +
      `&client_secret=${environment.team.clientSecret}` +
      `&redirect_uri=${window.location.origin}` +
      `&scope=${teamConfig.scope}` +
      `&code=${params['code']}` +
      `&code_verifier=${sessionStorage.getItem('c_ver')}` +
      `&grant_type=${teamConfig.grantType}`;

    let headers = { headers: { 'Content-Type': 'application/json' } };

    return this.http.post(`${environment.team.teamUrl}/oauth/token`, body, headers).pipe(
      map(data => {
        sessionStorage.setItem('team-token', JSON.stringify(data));
        return data;
      }),
      mergeMap(token => this.getUserInfo(token)),
      mergeMap(data => this.storeUser('TEAM', data)),
      mergeMap(data => this.getLocations())
    );
  }
*/
  teamAuth(params: any) {
    const body = {
      client_id: environment.team.clientId,
      //client_secret: environment.team.clientSecret,
      redirect_uri: window.location.origin,
      scope: teamConfig.scope,
      code: params['code'],
      code_verifier: sessionStorage.getItem('c_ver'),
      grant_type: teamConfig.grantType
    };

    // Solo agregar X-Token-Issuer si la URL es la API interna (environment.apiURL)
    const url = `${environment.team.teamUrl}/oauth/token`;
    let apiURL = environment.apiUrl;
    let headers = { headers: { 'Content-Type': 'application/json' } };

    return this.http
      .post(url, body, headers)
      .pipe(
        map(response => {
          sessionStorage.setItem('team-token', JSON.stringify(response));
          return response;
        }),
      mergeMap(teamToken => this.storeUser('TEAM', teamToken)),
      mergeMap(data => this.getLocations())
      );
    }


    getUserInfo(token) {
   
    //let headers: Record<string, string> = { Authorization: `Bearer ${token.access_token}` };
    let headers = { headers: { Authorization: `Bearer ${token.access_token} ` } };
    console.log('headers authservice:', token.access_token);
    return this.http.post(`${environment.team.teamUrl}/userinfo`,{}, headers).pipe(map(data => ({ data, token })));
  

  }

  adAuth(data) {
    return this.storeUser('AD', { data }).pipe(mergeMap(data => this.getLocations()));
  }

  checkRol(rol) {
    switch (rol) {
      case 'such':
      case 'guest':
        return true;
      default:
        return false;
    }
  }

  async generateCodeChallenge(codeVerifier) {
    const myBitArray = sjcl.hash.sha256.hash(codeVerifier);
    const myHash = sjcl.codec.base64.fromBits(myBitArray);
    return myHash
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  getLocations(userData = {}) {
    // return of(mockUser)
    return this.http.get<User>(`${environment.apiUrl}/api/auth`)
      .pipe(
        map((response: any) => {
          let id = 0;
          response.locationRoles.forEach(role => {
            role.Selected = false;
            role.Id = id;
            id++;
          });
          const userStr = localStorage.getItem('user');
          const userStorage = userStr ? JSON.parse(userStr) : {};
          userStorage.locationRoles = response.locationRoles;
          localStorage.setItem('user', JSON.stringify(userStorage));
          this.currentUserSubject.next(userStorage);
          return of(response.locationRoles);
        })
      );
  }

  storeUser(type: any, authResponse: any = null) {
    let userStorage: any;
    if (type === 'AD') {
      userStorage = {
        name: authResponse['data'].account.name,
        username: authResponse['data'].account.username,
        token: authResponse['data'].idToken,
        exp: authResponse['data'].idTokenClaims?.exp,
        provider: 'AD',
        authPayload: authResponse['data'],
        email: authResponse['data'].account.username,
        access: authResponse['data'].accessToken,
        expiresAt: authResponse['data'].idTokenClaims?.exp ? authResponse['data'].idTokenClaims.exp * 1000 : null
      };
    }
    else {
      const decodedIdToken = authResponse?.id_token ? this.jwtHelper.decodeToken(authResponse.id_token) : null;
      const expiresIn = Number(authResponse?.expires_in ?? 0);
      const expiresAtFromToken = decodedIdToken?.exp ? Number(decodedIdToken.exp) * 1000 : null;
      const adUsername = decodedIdToken?.nickname ? `SSN\\${decodedIdToken.nickname}` : null;
      const fullName = decodedIdToken?.name
        ? decodedIdToken.name
        : `${decodedIdToken?.given_name ?? ''} ${decodedIdToken?.family_name ?? ''}`.trim();

      const teamPayload = {
        token: authResponse,
        data: decodedIdToken
      };

      userStorage = {
        name: fullName,
        username: decodedIdToken?.nickname,
        token: authResponse?.access_token,
        exp: authResponse?.expires_in,
        provider: 'TEAM',
        ad_username: decodedIdToken?.ad_username ?? adUsername,
        authPayload: teamPayload,
        // email: user['data'].website,
        email: decodedIdToken?.website ?? decodedIdToken?.nickname,
        expiresAt: expiresAtFromToken ?? (expiresIn > 0 ? new Date(new Date().toUTCString()).getTime() + expiresIn * 1000 : null)
      };
    }

    this.diagnosticService.setUser(userStorage.name);
    localStorage.setItem('user', JSON.stringify(userStorage));
    this.currentUserSubject.next(userStorage);
    return of(authResponse);
  }

  logoutProv(prov: string = null) {
    const provider = this.currentUserValue?.provider;
    this.clearStores();
    if ([provider, prov].includes('AD')) {
      this.msalService.logoutRedirect();
    }
    else {
      // this.oauthService.logOut();
      // this.router.navigateByUrl('/login');
      window.location.href = environment.team.logoutUrl;
    }
    return of(true);
  }

  logout() {
    if (this.appService.inPortal()) {
      this.clearStores();
      this.oauthService.logOut();
      this.router.navigateByUrl('/login');
    }
    else {
      this.clearStores();
      this.msalService.logoutRedirect();
    }
  }

  clearStores(key?: string) {
    if (key) {
      localStorage.removeItem(key);
    }
    else {
      localStorage.removeItem('user');
      localStorage.removeItem('token_expiresOn');
      sessionStorage.removeItem('team-token');
      sessionStorage.removeItem('c_ver');
    }

    this.filterService.clearStorage();
    this.dynamicTableService.clearStorage();
    this.documentsService.clearStorage();
    this.textFiltersService.clearStorage();
    this.currentUserSubject.next(null);
  }

  loginRedirectAd() {
    this.diagnosticService.setMethodName("auth.service.loginRedirectAd()")  

    this.ensureMsalInitialized().then(() => {
      if (this.msalGuardConfig?.authRequest) {
        this.diagnosticService.setTrace("auth.service.loginRedirectAd().authRequest");
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        this.diagnosticService.setTrace("auth.service.loginRedirectAd() is not authRequest");
        this.msalService.loginRedirect();
      }
    }).catch((error) => {
      console.error('Error initializing MSAL before login redirect:', error);
    });
  }

  checkUserSession() {
    const now = moment().unix();
    const duration = moment.duration(moment.unix(now).diff(moment.unix(this.currentUserValue['exp'])));
    return duration.asMinutes() > -5 ? true : false;
  }

  refreshToken() {
    this.ensureMsalInitialized().then(() => {
      const account = this.msalService.instance.getAllAccounts()[0];

      if (!account) {
        console.warn('No account available for token refresh.');
        return;
      }

      const accessTokenRequest = {
        scopes: ['https://graph.microsoft.com/User.Read','user.read'],
        account: account,
        forceRefresh: true
      }

      this.msalService.instance.acquireTokenSilent(accessTokenRequest).then(response => {
        this.setUserStorage(response);
      }).catch(error => {
        if (error instanceof InteractionRequiredAuthError) {
          this.msalService.instance.acquireTokenRedirect(accessTokenRequest);
        }
      });
    }).catch(error => {
      console.error('Error initializing MSAL before refreshToken:', error);
    });
  }

  /**
 * Verifica si el token es válido y, si está vencido, lo renueva silenciosamente.
 * Devuelve el nuevo token si fue exitoso, o null si falló.
 */
public async verifyAndRenewToken(): Promise<string | null> {
  try {
    await this.ensureMsalInitialized();
  } catch (error) {
    console.error('❌ Error initializing MSAL in verifyAndRenewToken:', error);
    return null;
  }

  const accounts = this.msalService.instance.getAllAccounts();

  if (accounts.length === 0) {
    console.warn('⚠️ No se encontraron cuentas activas.');
    return null;
  }

  const tokenExpiresOn = this.getTokenExpirationDate();
  const now = new Date();

  // Si el token sigue vigente
  if (tokenExpiresOn && tokenExpiresOn > now) {
    console.log('✅ Token aún válido, sin necesidad de renovación');
    this.restartTokenRefreshWorker();
    return this.currentUserValue?.token ?? null;
  }

  // Si está vencido, intenta refrescar
  try {
    const success = await firstValueFrom(this.refreshTokenv2(accounts[0].username));

    if (success) {
      console.log('🔄 Token renovado correctamente');
      this.restartTokenRefreshWorker();

      // Aseguramos devolver el nuevo token actualizado
      const newUser = this.currentUserValue;
      return newUser?.token ?? null;
    } else {
      console.error('❌ Error durante la renovación del token (success=false)');
      return null;
    }
  } catch (error) {
    console.error('❌ Error durante verifyAndRenewToken:', error);
    return null;
  }
}

public getTokenExpirationDate(): Date | null {
    const dateStr = localStorage.getItem('token_expiresOn');
    if (dateStr) {
      return new Date(dateStr);
    } else {
      return null
    }

  }

   public refreshTokenv2(username: string): Observable<boolean> {
  const result = new ReplaySubject<boolean>(1); // ✅ evita pérdida de emisión

  this.ensureMsalInitialized().then(() => {
    const account = this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      result.next(false);
      result.complete();
      return;
    }

    const accessTokenRequest = {
      scopes: ['https://graph.microsoft.com/User.Read','user.read'],
      account,
      forceRefresh: true
    };

    this.msalService.instance.acquireTokenSilent(accessTokenRequest)
      .then((response: any) => {
        this.msalService.instance.setActiveAccount(response.account);
        this.setUserStorage(response);
        this.restartTokenRefreshWorker();
        result.next(true);
        result.complete();
      })
      .catch((error: any) => {
        console.log('error catch refreshTokenv2', error);
        if (error instanceof InteractionRequiredAuthError) {
          this.msalService.instance.acquireTokenRedirect(accessTokenRequest);
        }
        result.next(false);
        result.complete();
      });
  }).catch((error: any) => {
    console.error('Error initializing MSAL before refreshTokenv2:', error);
    result.next(false);
    result.complete();
  });

  return result.asObservable();
}

  private async ensureMsalInitialized(): Promise<void> {
    if (!this.msalInitPromise) {
      this.msalInitPromise = this.msalService.instance.initialize()
        .then(() => this.msalService.instance.handleRedirectPromise())
        .then((response) => {
          if (response?.account) {
            this.msalService.instance.setActiveAccount(response.account);
          }
        })
        .catch((error) => {
          this.msalInitPromise = null;
          throw error;
        });
    }

    return this.msalInitPromise;
  }

private restartTokenRefreshWorker() {
    const tokenExpiresOn = this.getTokenExpirationDate();

    // Verificar si hay una fecha de caducidad válida
    if (tokenExpiresOn) {
      this.startTokenRefreshWorker(tokenExpiresOn);
    }
  }

  async startTokenRefreshWorker(tokenExpiresOn: Date) {
    if (this.tokenRefreshTimeoutId) {
      clearTimeout(this.tokenRefreshTimeoutId);
      this.tokenRefreshTimeoutId = null;
    }

    const now = new Date().getTime();
    const refreshAt = new Date(tokenExpiresOn).getTime() - (60 * 1000);
    const delay = Math.max(0, refreshAt - now);

    this.tokenRefreshTimeoutId = setTimeout(async () => {
      const accounts = this.msalService.instance.getAllAccounts();

      if (accounts.length === 0 || !accounts[0].username) {
        console.error('Cannot refresh token: no account or username available');
        return;
      }

      try {
        const success = await firstValueFrom(this.refreshTokenv2(accounts[0].username));
        if (success) {
          console.log('Token refreshed successfully via scheduler');
        } else {
          console.error('Error during token refresh: refreshTokenv2 returned false');
        }
      } catch (error) {
        console.error('Error during token refresh via scheduler:', error);
      }
    }, delay);

    console.log('Token refresh scheduler started. Next refresh in ms:', delay);
  }

  terminateTokenRefreshWorker() {
    if (this.tokenRefreshTimeoutId) {
      clearTimeout(this.tokenRefreshTimeoutId);
      this.tokenRefreshTimeoutId = null;
    }

    if (this.tokenRefreshWorker) {
      this.tokenRefreshWorker.terminate();
      this.tokenRefreshWorker = null;
    }
  }

  setUserStorage(session) {
    if (session.idToken && session.idToken.rawIdToken === this.currentUserValue.token) return;
    const user = {
      ...this.currentUserValue,
      exp: session.idTokenClaims.exp,
      authPayload: session,
      token: session.idToken,
      access: session.accessToken 
    }
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token_expiresOn', session.expiresOn?.toISOString() ?? "");
    this.currentUserSubject.next(user);
  }

  compareUsr(userSigner: any): boolean {
    const prop = this.currentUserValue.provider === 'AD' ? userSigner.Email : userSigner.UserFullName;
    const adUsername = this.currentUserValue?.ad_username ? this.currentUserValue.ad_username?.toUpperCase() : this.currentUserValue?.email?.toUpperCase();
    return prop.toUpperCase().includes(adUsername);
  }

  currentLocation() {
    const cUser = this.currentUserValue;
    if (cUser && cUser.selectedLocationIndex != null && cUser.selectedLocationIndex != undefined && cUser.selectedLocationIndex.toString() && cUser.locationRoles) {
      return cUser.locationRoles[cUser.selectedLocationIndex];
    }
    return null;
  }

  hasSigners() {
    return this.currentLocation().Signers;
  }
}